import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { permissions } from '@/lib/permissions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageLoading } from '@/components/ui/loading';
import { getStatusBadgeClass, getStatusLabel, formatDate } from '@/lib/utils';
import { ArrowLeft, Edit, Send, CheckCircle } from 'lucide-react';
import { useState } from 'react';
import DecisionModal from '@/components/common/DecisionModal';
import ApprovalHistory from '@/components/common/ApprovalHistory';

export default function RequirementsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [actionType, setActionType] = useState<'manager-decision' | 'cluster-decision' | null>(null);
  const [remarks, setRemarks] = useState('');
  const [serverError, setServerError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['requirement', id],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: any }>(`/requirements/${id}`);
      return res.data;
    },
    enabled: !!id,
  });

  const submitMutation = useMutation({
    mutationFn: () => api.post(`/requirements/${id}/submit`, {}),
    onSuccess: () => {
      addToast('Submitted for Manager approval', 'success');
      queryClient.invalidateQueries({ queryKey: ['requirements'] });
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['report-project-summary'] });
      queryClient.invalidateQueries({ queryKey: ['report-category-detail'] });
      queryClient.invalidateQueries({ queryKey: ['report-budget-actual'] });
      queryClient.invalidateQueries({ queryKey: ['report-multi-summary'] });
      queryClient.invalidateQueries({ queryKey: ['requirement', id] });
    },
    onError: (err: any) => addToast(err.message || 'Failed', 'error'),
  });

  const managerDecisionMutation = useMutation({
    mutationFn: (params: { decision: 'APPROVE' | 'REJECT'; remarks: string }) => api.post(`/requirements/${id}/manager-decision`, params),
    onSuccess: (_data, { decision }) => {
      const msg = decision === 'APPROVE' ? 'Departmental Labour-Requirement approved by Manager and sent to Cluster Head.' : 'Departmental Labour-Requirement rejected and returned to the Engineer.';
      addToast(msg, decision === 'APPROVE' ? 'success' : 'info');
      queryClient.invalidateQueries({ queryKey: ['requirements'] });
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['report-project-summary'] });
      queryClient.invalidateQueries({ queryKey: ['report-category-detail'] });
      queryClient.invalidateQueries({ queryKey: ['report-budget-actual'] });
      queryClient.invalidateQueries({ queryKey: ['report-multi-summary'] });
      queryClient.invalidateQueries({ queryKey: ['requirement', id] });
      setActionType(null); setRemarks(''); setServerError('');
    },
    onError: (err: any) => { setServerError(err.message || 'Failed to save decision'); },
  });

  const clusterDecisionMutation = useMutation({
    mutationFn: (params: { decision: 'APPROVE' | 'REJECT'; remarks: string }) => api.post(`/requirements/${id}/cluster-decision`, params),
    onSuccess: (_data, { decision }) => {
      const msg = decision === 'APPROVE' ? 'Departmental Labour-Requirement finally approved.' : 'Departmental Labour-Requirement rejected by Cluster Head.';
      addToast(msg, decision === 'APPROVE' ? 'success' : 'info');
      queryClient.invalidateQueries({ queryKey: ['requirements'] });
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['report-project-summary'] });
      queryClient.invalidateQueries({ queryKey: ['report-category-detail'] });
      queryClient.invalidateQueries({ queryKey: ['report-budget-actual'] });
      queryClient.invalidateQueries({ queryKey: ['report-multi-summary'] });
      queryClient.invalidateQueries({ queryKey: ['requirement', id] });
      setActionType(null); setRemarks(''); setServerError('');
    },
    onError: (err: any) => { setServerError(err.message || 'Failed to save decision'); },
  });

  const isApprovePending =
    (managerDecisionMutation.isPending && managerDecisionMutation.variables?.decision === 'APPROVE') ||
    (clusterDecisionMutation.isPending && clusterDecisionMutation.variables?.decision === 'APPROVE');
  const isRejectPending =
    (managerDecisionMutation.isPending && managerDecisionMutation.variables?.decision === 'REJECT') ||
    (clusterDecisionMutation.isPending && clusterDecisionMutation.variables?.decision === 'REJECT');
  const loadingAction = isApprovePending ? 'APPROVE' : isRejectPending ? 'REJECT' : null;

  const handleDecision = (decision: 'APPROVE' | 'REJECT', remarksValue: string) => {
    setServerError('');
    const payload = { decision, remarks: remarksValue };
    if (actionType === 'manager-decision') managerDecisionMutation.mutate(payload);
    else clusterDecisionMutation.mutate(payload);
  };

  if (isLoading || !data) return <PageLoading />;

  const roles = user?.roles ?? [];
  const canEdit = permissions.canEditOperationalRecord(roles, data, user?.id);
  const canSubmit = permissions.canSubmitOperationalRecord(roles, data, user?.id);
  // Only the assigned Project Manager may review a PENDING record. Legacy
  // records with no assigned PM (NULL) remain reviewable by any Manager.
  const isAssignedPm = !data.assignedPmUserId || data.assignedPmUserId === user?.id;
  const canManagerDecision = data.status === 'PENDING_MANAGER_APPROVAL' && permissions.canManagerDecide(roles) && data.createdBy !== user?.id && isAssignedPm;
  const canClusterDecision = data.status === 'MANAGER_APPROVED' && permissions.canClusterDecide(roles) && data.createdBy !== user?.id;

  const grandTotal = data.items?.reduce((sum: number, item: any) => sum + (item.total || 0), 0) || 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/requirements')}><ArrowLeft size={18} /></Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-heading font-bold text-primary">{data.requirementNumber}</h1>
          <p className="text-secondary break-words">{data.projectName} &middot; {formatDate(data.startDate)} - {formatDate(data.endDate)}</p>
        </div>
        <Badge variant={getStatusBadgeClass(data.status) as any} className="text-sm px-3 py-1">{getStatusLabel(data.status)}</Badge>
        {canEdit && <Link to={`/requirements/${id}/edit`}><Button variant="outline" size="sm"><Edit size={14} className="mr-1" />Edit</Button></Link>}
        {canSubmit && <Button size="sm" onClick={() => { if (!data.assignedPmUserId) { addToast('Select a Project Manager before submitting this requirement for approval.', 'error'); return; } submitMutation.mutate(); }} loading={submitMutation.isPending}><Send size={14} className="mr-1" />Submit</Button>}
        {canManagerDecision && <Button size="sm" onClick={() => { setActionType('manager-decision'); setRemarks(''); setServerError(''); }}><CheckCircle size={14} className="mr-1" />Review</Button>}
        {canClusterDecision && <Button size="sm" onClick={() => { setActionType('cluster-decision'); setRemarks(''); setServerError(''); }}><CheckCircle size={14} className="mr-1" />Review</Button>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="min-w-0"><span className="text-secondary">Project</span><p className="font-medium break-words">{data.projectName}</p></div>
            <div className="min-w-0"><span className="text-secondary">Start Date</span><p className="font-medium">{formatDate(data.startDate)}</p></div>
            <div className="min-w-0"><span className="text-secondary">End Date</span><p className="font-medium">{formatDate(data.endDate)}</p></div>
            <div className="min-w-0"><span className="text-secondary">Block</span><p className="font-medium break-words">{data.blockName || '-'}</p></div>
            <div className="min-w-0"><span className="text-secondary">Activity</span><p className="font-medium break-words">{data.activityName || '-'}</p></div>
            <div className="min-w-0"><span className="text-secondary">Unit</span><p className="font-medium">{data.unitName || '-'}</p></div>
            <div className="min-w-0"><span className="text-secondary">Qty</span><p className="font-medium">{data.quantity || '-'}</p></div>
            <div className="min-w-0"><span className="text-secondary">Created By</span><p className="font-medium break-words">{data.createdByName}</p></div>
            <div className="min-w-0 sm:col-span-2"><span className="text-secondary">Assigned Project Manager</span><p className="font-medium break-words">{data.assignedPmName ? `${data.assignedPmName}${data.assignedPmEmail ? ` (${data.assignedPmEmail})` : ''}` : <span className="text-muted">Not assigned</span>}</p></div>
            {data.grandTotal !== undefined && <div className="min-w-0"><span className="text-secondary">Grand Total</span><p className="font-medium">&#8377;{grandTotal.toLocaleString('en-IN')}</p></div>}
            {data.notes && <div className="col-span-1 sm:col-span-2 min-w-0"><span className="text-secondary">General Remarks</span><p className="font-medium break-words">{data.notes}</p></div>}
          </CardContent>
        </Card>

        <ApprovalHistory history={data.approvalHistory} />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Worker Categories</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Worker Category</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items?.map((item: any) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.workerCategoryName || item.categoryName || item.tradeName}</TableCell>
                    <TableCell className="text-right">{item.count || item.requiredHeadcount}</TableCell>
                    <TableCell className="text-right">&#8377;{(item.rate || item.plannedRate || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-right">&#8377;{(item.total || item.plannedAmount || 0).toLocaleString('en-IN')}</TableCell>
                  </TableRow>
                ))}
                {(!data.items || data.items.length === 0) && (
                  <TableRow><TableCell colSpan={4} className="text-center py-4 text-muted">No items</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="md:hidden divide-y divide-border">
            {data.items?.length > 0 ? data.items.map((item: any) => (
              <div key={item.id} className="p-4 space-y-2">
                <p className="font-medium text-primary break-words">{item.workerCategoryName || item.categoryName || item.tradeName}</p>
                <dl className="text-sm space-y-1">
                  <div className="flex justify-between gap-3"><dt className="text-secondary">Count</dt><dd className="text-primary text-right">{item.count || item.requiredHeadcount}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-secondary">Rate</dt><dd className="text-primary text-right">&#8377;{(item.rate || item.plannedRate || 0).toLocaleString('en-IN')}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-secondary">Total</dt><dd className="text-primary text-right font-medium">&#8377;{(item.total || item.plannedAmount || 0).toLocaleString('en-IN')}</dd></div>
                </dl>
              </div>
            )) : (
              <p className="text-center py-4 text-muted">No items</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="py-4">
          <div className="flex justify-end text-lg font-semibold">
            Grand Total: &#8377;{grandTotal.toLocaleString('en-IN')}
          </div>
        </CardContent>
      </Card>

      <DecisionModal
        open={!!actionType}
        title={actionType === 'manager-decision' ? 'Manager Decision' : 'Cluster Head Decision'}
        recordLabel="Departmental Labour-Requirement:"
        recordNumber={data.requirementNumber}
        engineerName={data.createdByName}
        projectName={data.projectName}
        remarks={remarks}
        onRemarksChange={(value) => { setRemarks(value); setServerError(''); }}
        serverError={serverError}
        loadingAction={loadingAction}
        onApprove={(remarksValue) => handleDecision('APPROVE', remarksValue)}
        onReject={(remarksValue) => handleDecision('REJECT', remarksValue)}
        onCancel={() => { setActionType(null); setRemarks(''); setServerError(''); }}
      />
    </div>
  );
}

